import {
  Outlet,
  Route,
  createRoutesFromElements,
  redirect,
  useRouteLoaderData,
  type LoaderFunction,
  type LoaderFunctionArgs,
} from 'react-router-dom';
import {
  ProductLandingPage,
  ReportLayout,
  OverviewPage,
  EngagementFeedPage,
  LexilePage,
  GEPage,
  ComingSoonPage,
  DataErrorBoundary,
  isSupportedProduct,
  getReportsForProduct,
  validateDateRangeParams,
  applicationsQuery as fulfillmentApplicationsQuery,
  type ReportPath,
} from 'eds-data-ux';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { toast } from '@scholastic/mockingjay';
import { ApiError, MAGAZINE_GROUP_CODE } from 'eds-common';
import {
  meQueryOptions,
  meResponseShape,
  getProductByAccessCodeApi,
  getProductByAccessCodeApiQueryKey,
  getProductByProductCodeApi,
  getPermissionsApi,
  getPermissionsApiQueryKey,
} from 'eds-common/api/v2';
import { getClassesApi, getClassesApiQueryKey } from 'eds-common/api/v3';
import { AppLayout } from '@/pages/AppLayout/AppLayout';
import { RootLayout } from '@/pages/RootLayout/RootLayout';
import { rosterUserQuery } from '@/hooks/api/use-roster-user/use-roster-user';
import { SignOutPage } from '@/pages/SignOutPage/SignOutPage';
import { ErrorBoundary } from '@/components/ErrorBoundary/ErrorBoundary';
import { signOutAction } from '@/services/app-session/sign-out-action';
import { clientEnvsQuery } from './hooks/api/use-client-envs/use-client-envs';
import { PAGE_TYPE, SITE_SECTION } from '@/constants';
import { AppProvider } from '@/contexts/AppContext/AppContext';
import { applicationsQuery } from '@/hooks/api/use-application/use-application';
import { entitlementsQuery } from '@/hooks/api/use-entitlements/use-entitlements';
import { hasOnlyKlAccess } from '@/utils/application-utils';
import { customJwtQuery } from '@/hooks/api/use-custom-jwt/use-custom-jwt';
import { getApplicationLaunchRoute } from '@/util/getApplicationLaunchRoute';
import { RedirectError } from '@/errors/RedirectError';
import type { DeepLinkNoAccessState } from '@/components/AccessCodeRedemptionDialog/DeepLinkNoAccessDialog';
import {
  ACCESS_CODE_QUERY_PARAM,
  ACCESS_CODE_STORAGE_KEY,
  APP_QUERY_PARAM,
  APP_STATE_STORAGE_KEY,
  APP_STORAGE_KEY,
  REDEEM_STORAGE_KEY,
  STATE_QUERY_PARAM,
  clearAccessCodeDeepLink,
  consumeStoredAppDeepLink,
  hasApplicationAccess,
  isEntitledToProductCode,
  isRedeemDeepLinkRequested,
} from '@/components/AccessCodeRedemptionDialog/access-code-deep-link';

export const queryClient = new QueryClient();
const storyPageTitle = 'Story Page';

// Helper function to check access for student work routes
async function checkAccessForStudentWorkRoutes(request: Request) {
  if (request.url.includes('/work')) {
    try {
      const applications = await queryClient.ensureQueryData(
        applicationsQuery()
      );
      // Block if user has ONLY 'KL' access
      if (hasOnlyKlAccess(applications)) {
        // Redirect back to base URL
        const cleanUrl = request.url.split('/work')[0];
        if (cleanUrl) {
          return redirect(cleanUrl);
        }
      }
    } catch (error) {
      const cleanUrl = request.url.split('/work')[0];
      if (cleanUrl) {
        return redirect(cleanUrl);
      }
    }
  }
  return null;
}

const ROLE_REDIRECT_PATHS = {
  teacher: '/',
  administrator: '/',
  student: '/student',
  classPassword: '/student',
  site_auth: '/siteauth',
  site_auth_admin: '/siteauth',
} as const;

function composeLoaders(...loaders: LoaderFunction[]) {
  return async (args: LoaderFunctionArgs) => {
    const results = await Promise.all(loaders.map((loader) => loader(args)));
    return results.reduce((acc, result) => ({ ...acc, ...result }), {});
  };
}

function encodeAppState(state: string | null) {
  return state ? encodeURIComponent(state) : undefined;
}

function isUnauthenticated(error: unknown) {
  return (
    error instanceof ApiError &&
    (error.cause.status === 401 || error.cause.status === 403)
  );
}

async function authLoaderWithSessionContext() {
  const user = await queryClient.ensureQueryData(meQueryOptions());
  return { user };
}

async function authLoader() {
  const meResponse = await queryClient.ensureQueryData(meQueryOptions());
  return { user: meResponse.userContext };
}

function roleGuardLoader(allowedRoles: (keyof typeof ROLE_REDIRECT_PATHS)[]) {
  return async () => {
    const { user } = await authLoader();
    const { currentRole } = user;
    if (!(currentRole in ROLE_REDIRECT_PATHS)) {
      return redirect('/sign-in');
    }
    const role = currentRole as keyof typeof ROLE_REDIRECT_PATHS;
    if (!allowedRoles.includes(role)) {
      return redirect(ROLE_REDIRECT_PATHS[role]);
    }
    return { user };
  };
}

function launchStoredDeepLinkIfPresent() {
  const storedApp = sessionStorage.getItem(APP_STORAGE_KEY);
  const storedAppState = sessionStorage.getItem(APP_STATE_STORAGE_KEY);
  if (storedApp) {
    throw new RedirectError(
      getApplicationLaunchRoute(storedApp, false, storedAppState ?? undefined)
    );
  }
}

// Resolves a stored `?app=` deep link: launch it when the user has access,
// otherwise signal the no-access dialog (never launch an un-entitled app).
// Students get the same launch/no-access resolution as staff, but always the
// `general` no-access variant — they can't redeem access codes, so the
// magazine "add product" path never applies to them.
async function resolveAppDeepLink({ student = false } = {}) {
  const storedApp = sessionStorage.getItem(APP_STORAGE_KEY);
  if (!storedApp) {
    return {
      accessCodeRedemptionInitialState: null,
      appDeepLinkNoAccess: null,
      pendingAppLaunch: null,
    };
  }

  // A user with zero apps 404s here
  const applications = await queryClient
    .ensureQueryData(applicationsQuery())
    .catch(() => []);

  if (hasApplicationAccess(applications, storedApp)) {
    launchStoredDeepLinkIfPresent();
    return {
      accessCodeRedemptionInitialState: null,
      appDeepLinkNoAccess: null,
      pendingAppLaunch: null,
    };
  }

  // No access
  const pendingAppLaunch = consumeStoredAppDeepLink();

  // Students can't redeem, so skip the magazine lookup and always show the
  // general no-access dialog.
  if (student) {
    return {
      accessCodeRedemptionInitialState: null,
      appDeepLinkNoAccess: { variant: 'general' } as DeepLinkNoAccessState,
      pendingAppLaunch: null,
    };
  }

  const product = await getProductByProductCodeApi(storedApp).catch(() => null);
  const appDeepLinkNoAccess: DeepLinkNoAccessState =
    product && product.groupCode === MAGAZINE_GROUP_CODE
      ? { variant: 'magazine', productName: product.productName }
      : { variant: 'general' };

  return {
    accessCodeRedemptionInitialState: null,
    appDeepLinkNoAccess,
    pendingAppLaunch,
  };
}

async function homeDeepLinkLoader() {
  const user = (await queryClient.ensureQueryData(meQueryOptions()))
    .userContext;

  // Only resolve the deep link for roles whose home route is `/`. Other roles
  // are redirected away by the parent guard and resolve it on their own home
  // route; this loader runs in parallel with that redirect, so bail before
  // touching sessionStorage.
  const role = user.currentRole as keyof typeof ROLE_REDIRECT_PATHS;
  if (ROLE_REDIRECT_PATHS[role] !== '/') {
    return {
      accessCodeRedemptionInitialState: null,
      appDeepLinkNoAccess: null,
      pendingAppLaunch: null,
    };
  }

  const redeemRequested = Boolean(sessionStorage.getItem(REDEEM_STORAGE_KEY));

  const accessCode = sessionStorage.getItem(ACCESS_CODE_STORAGE_KEY);
  if (accessCode) {
    clearAccessCodeDeepLink();

    const entitlements = await queryClient.ensureQueryData(
      entitlementsQuery(user.userId)
    );

    const product = await queryClient
      .fetchQuery({
        queryKey: getProductByAccessCodeApiQueryKey(accessCode),
        queryFn: () => getProductByAccessCodeApi(accessCode),
      })
      .catch(() => {
        toast.error('The access code is expired or does not exist.', {
          position: 'top-center',
        });
        return null;
      });

    if (!product) {
      return {
        accessCodeRedemptionInitialState: null,
        appDeepLinkNoAccess: null,
        openAccessCodeRedemption: redeemRequested,
        pendingAppLaunch: null,
      };
    }

    if (!isEntitledToProductCode(entitlements, product.productCode)) {
      return {
        accessCodeRedemptionInitialState: { accessCode, product },
        appDeepLinkNoAccess: null,
        pendingAppLaunch: consumeStoredAppDeepLink(),
      };
    }

    return resolveAppDeepLink();
  }

  if (redeemRequested) {
    clearAccessCodeDeepLink();
    return {
      accessCodeRedemptionInitialState: null,
      appDeepLinkNoAccess: null,
      openAccessCodeRedemption: true,
      pendingAppLaunch: consumeStoredAppDeepLink(),
    };
  }

  return resolveAppDeepLink();
}

async function studentHomeDeepLinkLoader() {
  sessionStorage.removeItem(REDEEM_STORAGE_KEY);
  return resolveAppDeepLink({ student: true });
}

// Checks if the report is configured for the current product.
// Also applies product-code-level exclusions (e.g. R4RWB-only users cannot access ge-score).
function reportGuardLoader(reportPath: ReportPath) {
  return async ({ params }: LoaderFunctionArgs) => {
    const productId = params.productId!;
    const applications = await queryClient.ensureQueryData(
      fulfillmentApplicationsQuery()
    );
    const userProductCodes =
      applications
        .find((app) => app.applicationCode === productId)
        ?.products.map((p) => p.productCode) ?? [];

    const allowed = getReportsForProduct(productId, userProductCodes).map(
      (r) => r.path
    );
    if (!allowed.includes(reportPath)) {
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      throw new Response('Not Found', { status: 404 });
    }
    return null;
  };
}

// Fetch the authenticated user's classes (cached via react-query).
async function fetchUserClasses() {
  const { user } = await authLoader();
  return queryClient.ensureQueryData({
    queryKey: getClassesApiQueryKey({
      userId: user.userId,
      orgId: user.currentOrg.orgId,
      source: user.userSource,
      studentsExists: true,
    }),
    queryFn: () =>
      getClassesApi({
        userId: user.userId,
        orgId: user.currentOrg.orgId,
        source: user.userSource,
        studentsExists: true,
      }),
  });
}

// Requires exactly 1 classId that exists in the user's classes. Returns the matched class.
async function validateSingleClass(
  reportPath: ReportPath,
  args: LoaderFunctionArgs
) {
  // Throws 404 if from/to date are invalid
  validateDateRangeParams(args.request);
  // Throws 404 if report not allowed for this product
  await reportGuardLoader(reportPath)(args);
  const classIds = new URL(args.request.url).searchParams.getAll('classId');
  if (classIds.length !== 1) {
    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    throw new Response('Not Found', { status: 404 });
  }
  const classes = await fetchUserClasses();
  const matchedClass = classes.find((cls) => cls.class_id === classIds[0]);
  if (!matchedClass) {
    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    throw new Response('Not Found', { status: 404 });
  }
  return matchedClass;
}

// All-classes view: classIds are optional, but if provided they must all be valid.
function allClassesViewGuardLoader(reportPath: ReportPath) {
  return async (args: LoaderFunctionArgs) => {
    // Throws 404 if from/to date are invalid
    validateDateRangeParams(args.request);
    // Throws 404 if report not allowed for this product
    await reportGuardLoader(reportPath)(args);
    const classIds = new URL(args.request.url).searchParams.getAll('classId');
    if (classIds.length === 0) return null;
    const classes = await fetchUserClasses();
    const classIdSet = new Set(classes.map((cls) => cls.class_id));
    if (!classIds.every((id) => classIdSet.has(id))) {
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      throw new Response('Not Found', { status: 404 });
    }
    return null;
  };
}

// Engagement feed: classIds are optional; studentIds are optional but require exactly 1 classId
// and must belong to that class.
function engagementFeedGuardLoader(reportPath: ReportPath) {
  return async (args: LoaderFunctionArgs) => {
    // Throws 404 if from/to date are invalid
    validateDateRangeParams(args.request);
    await reportGuardLoader(reportPath)(args);
    const url = new URL(args.request.url);
    const classIds = url.searchParams.getAll('classId');
    const studentIds = url.searchParams.getAll('studentId');

    if (classIds.length > 0) {
      const classes = await fetchUserClasses();
      const classIdSet = new Set(classes.map((cls) => cls.class_id));
      if (!classIds.every((id) => classIdSet.has(id))) {
        // eslint-disable-next-line @typescript-eslint/no-throw-literal
        throw new Response('Not Found', { status: 404 });
      }

      // Students require exactly 1 class and must belong to it
      if (studentIds.length > 0) {
        if (classIds.length !== 1) {
          // eslint-disable-next-line @typescript-eslint/no-throw-literal
          throw new Response('Not Found', { status: 404 });
        }
        const matchedClass = classes.find(
          (cls) => cls.class_id === classIds[0]
        );
        if (
          !matchedClass ||
          !studentIds.every((id) => matchedClass.studentIds.includes(id))
        ) {
          // eslint-disable-next-line @typescript-eslint/no-throw-literal
          throw new Response('Not Found', { status: 404 });
        }
      }
    } else if (studentIds.length > 0) {
      // studentIds without any classId is invalid
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      throw new Response('Not Found', { status: 404 });
    }

    return null;
  };
}

// Class view: requires exactly 1 classId; studentIds are optional but must belong to the class.
function classViewGuardLoader(reportPath: ReportPath) {
  return async (args: LoaderFunctionArgs) => {
    const matchedClass = await validateSingleClass(reportPath, args);
    const studentIds = new URL(args.request.url).searchParams.getAll(
      'studentId'
    );
    if (studentIds.length > 0) {
      const allValid = studentIds.every((id) =>
        matchedClass.studentIds.includes(id)
      );
      if (!allValid) {
        // eslint-disable-next-line @typescript-eslint/no-throw-literal
        throw new Response('Not Found', { status: 404 });
      }
    }
    return null;
  };
}

// Student view: requires exactly 1 classId AND exactly 1 studentId that belongs to that class.
function studentViewGuardLoader(reportPath: ReportPath) {
  return async (args: LoaderFunctionArgs) => {
    const matchedClass = await validateSingleClass(reportPath, args);
    const { request } = args;
    const studentIds = new URL(request.url).searchParams.getAll('studentId');
    if (studentIds.length !== 1) {
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      throw new Response('Not Found', { status: 404 });
    }
    if (!matchedClass.studentIds.includes(studentIds[0] ?? '')) {
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      throw new Response('Not Found', { status: 404 });
    }
    return null;
  };
}

/**
 * Shared guard for data routes.
 * - 0 supported apps → allows through (product landing shows empty state)
 * - 1 supported app  → redirect straight to its overview report
 * - 2+ supported apps → returns (caller decides what to do next)
 */
async function ensureMultipleDataProducts() {
  const applications = await queryClient.ensureQueryData(
    fulfillmentApplicationsQuery()
  );
  const supportedApps = applications.filter((app) =>
    isSupportedProduct(app.applicationCode)
  );
  if (supportedApps.length === 1) {
    const app = supportedApps[0]!;
    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    throw redirect(`/data/${app.applicationCode}/overview`);
  }
}

/**
 * Layout gate for /data routes.
 * Admins see the Coming Soon page; teachers pass through to child routes.
 */
function DataRoleGate() {
  const data = useRouteLoaderData('data-layout') as
    | { user: { currentRole: string } }
    | undefined;
  if (data?.user.currentRole === 'administrator') {
    return <ComingSoonPage />;
  }
  return <Outlet />;
}

export const routes = createRoutesFromElements(
  <>
    <Route
      element={
        <QueryClientProvider client={queryClient}>
          <RootLayout />
        </QueryClientProvider>
      }
      errorElement={<ErrorBoundary />}
      loader={async ({ request }) => {
        const { searchParams } = new URL(request.url);
        const accessCode = searchParams.get(ACCESS_CODE_QUERY_PARAM);
        const app = searchParams.get(APP_QUERY_PARAM);
        const appState = encodeAppState(searchParams.get(STATE_QUERY_PARAM));

        if (accessCode) {
          sessionStorage.setItem(ACCESS_CODE_STORAGE_KEY, accessCode);
        }
        if (isRedeemDeepLinkRequested(searchParams)) {
          sessionStorage.setItem(REDEEM_STORAGE_KEY, 'true');
        }
        if (app) {
          sessionStorage.setItem(APP_STORAGE_KEY, app);
        }
        if (appState) {
          sessionStorage.setItem(APP_STATE_STORAGE_KEY, appState);
        }

        // Query params are consumed into sessionStorage above. The app-launch
        // vs. no-access decision is deferred to homeDeepLinkLoader (home is the
        // deep-link landing route) so an un-entitled `?app=` can surface the
        // no-access dialog instead of blindly redirecting into the launch.
        await queryClient.prefetchQuery(meQueryOptions());

        return null;
      }}
    >
      <Route
        element={
          <AppProvider>
            <AppLayout />
          </AppProvider>
        }
        loader={async ({ request }) => {
          // The one auth gate. Every route below it may assume a user, because
          // a redirect here short-circuits the navigation and discards their
          // loader errors. Any failure to obtain the user lands on sign-in.
          let user;
          try {
            user = (await queryClient.ensureQueryData(meQueryOptions()))
              .userContext;
          } catch (error) {
            if (!isUnauthenticated(error)) {
              console.error('Could not load the current user', error);
            }
          }
          if (!user) {
            const url = new URL(request.url);
            const returnUrl = `${url.pathname}${url.search}${window.location.hash}`;
            sessionStorage.setItem('returnUrl', returnUrl);
            return redirect(`/sign-in`);
          }
          const returnUrl = sessionStorage.getItem('returnUrl');
          if (returnUrl) {
            sessionStorage.removeItem('returnUrl');
            window.location.replace(returnUrl); // prevents future loaders from overriding returnUrl redirect
          }
          const knockJwt = await queryClient.ensureQueryData(
            customJwtQuery('KNOCK')
          );
          return { user, knockJwt };
        }}
      >
        <Route
          path="/sitemap"
          lazy={async () => {
            const { SiteMapPage } = await import(
              '@/pages/SiteMapPage/SiteMapPage'
            );

            return {
              element: <SiteMapPage />,
              handle: { title: 'Site Map' },
            };
          }}
        />
        <Route
          path="/knowledge-library"
          handle={{ siteSection: SITE_SECTION.productExperience }}
          loader={async () => {
            const user = await queryClient.ensureQueryData(meQueryOptions());
            if (user?.userContext.currentRole === 'student') {
              return redirect('/student');
            }
            return null;
          }}
        >
          <Route
            index
            lazy={async () => {
              const { KnowledgeLibraryLandingPage } = await import(
                'eds-product-experience-ux'
              );

              return {
                element: <KnowledgeLibraryLandingPage />,
                handle: {
                  title: 'Landing Page - Knowledge Library',
                  pageType: PAGE_TYPE.landingPage,
                },
              };
            }}
          />
          <Route
            path="topic/:bundleId"
            loader={async () => {
              const user = await queryClient.ensureQueryData(meQueryOptions());

              if (user?.userContext.currentRole === 'student') {
                return redirect('/student');
              }
              return user;
            }}
            lazy={async () => {
              const { EducatorTopicPageKL } = await import(
                'eds-product-experience-ux'
              );
              return {
                element: <EducatorTopicPageKL />,
                handle: {
                  pageType: PAGE_TYPE.bundle,
                },
              };
            }}
          />
          <Route
            path="book/:pcmId"
            loader={composeLoaders(authLoaderWithSessionContext, () => ({
              productCode: 'KL',
            }))}
            lazy={async () => {
              const { DefaultEducatorAssetPage } = await import(
                'eds-product-experience-ux'
              );
              return {
                element: <DefaultEducatorAssetPage />,
                handle: {
                  pageType: PAGE_TYPE.standaloneAsset,
                },
              };
            }}
          />
          <Route
            path="book/:assetFamilyId/resource/:pcmId"
            loader={async () => {
              const user = await queryClient.ensureQueryData(meQueryOptions());

              return {
                user,
                productCode: 'KL',
              };
            }}
            lazy={async () => {
              const { AssetFamilyPageKL } = await import(
                'eds-product-experience-ux'
              );

              return {
                element: <AssetFamilyPageKL />,
                handle: {
                  pageType: PAGE_TYPE.assetFamily,
                },
              };
            }}
          />
          <Route
            path="pages/:sectionId"
            loader={async () => {
              const user = await queryClient.ensureQueryData(meQueryOptions());
              if (user?.userContext.currentRole === 'student') {
                return redirect('/student');
              }
              return null;
            }}
            lazy={async () => {
              const { KnowledgeLibraryListPage } = await import(
                'eds-product-experience-ux'
              );
              return {
                element: <KnowledgeLibraryListPage />,
              };
            }}
          />
          <Route
            path="pages/:sectionId/part/:subsectionId"
            loader={async () => {
              const user = await queryClient.ensureQueryData(meQueryOptions());
              if (user?.userContext.currentRole === 'student') {
                return redirect('/student');
              }
              return null;
            }}
            lazy={async () => {
              const { KnowledgeLibraryListPage } = await import(
                'eds-product-experience-ux'
              );
              return {
                element: <KnowledgeLibraryListPage />,
              };
            }}
          />
        </Route>
        <Route
          path="/explore-ela"
          handle={{ siteSection: SITE_SECTION.productExperience }}
        >
          {/* Product Routes */}
          <Route
            index
            lazy={async () => {
              const { EducatorProductPage, StudentProductPage } = await import(
                'eds-product-experience-ux'
              );
              const user = await queryClient.ensureQueryData(meQueryOptions());
              if (user?.userContext.currentRole === 'student') {
                return {
                  element: <StudentProductPage />,
                  handle: {
                    title: 'Explore ELA Landing Page',
                    pageType: PAGE_TYPE.landingPage,
                  },
                };
              }
              return {
                element: <EducatorProductPage />,
                handle: {
                  title: 'Explore ELA Landing Page',
                  pageType: PAGE_TYPE.landingPage,
                },
              };
            }}
          />
          <Route
            path="resource/:assetId/*"
            loader={async ({ request }) => {
              const user = await queryClient.ensureQueryData(meQueryOptions());

              // Check access for student work routes
              const workRouteRedirect = await checkAccessForStudentWorkRoutes(
                request
              );
              if (workRouteRedirect) return workRouteRedirect;

              if (user?.userContext.currentRole === 'student') {
                const rosterUser = user
                  ? await queryClient.ensureQueryData(
                      rosterUserQuery(user.userContext.userId)
                    )
                  : undefined;
                const grade = rosterUser?.grade;
                return {
                  user,
                  grade,
                };
              }

              return {
                user,
              };
            }}
            lazy={async () => {
              const { TeacherSingleAssetPage, StudentSingleAssetPage } =
                await import('eds-product-experience-ux');
              const user = await queryClient.ensureQueryData(meQueryOptions());
              if (user?.userContext.currentRole !== 'student') {
                return {
                  element: <TeacherSingleAssetPage />,
                  handle: {
                    title: storyPageTitle,
                    pageType: PAGE_TYPE.standaloneAsset,
                  },
                };
              }
              return {
                element: <StudentSingleAssetPage />,
                handle: {
                  title: 'Student Single Story Page',
                  pageType: PAGE_TYPE.standaloneAsset,
                },
              };
            }}
          />

          <Route
            path="unit/:unitId"
            loader={async () => {
              const user = await queryClient.ensureQueryData(meQueryOptions());

              if (user?.userContext.currentRole === 'student') {
                return redirect('/student');
              }
              return user;
            }}
            lazy={async () => {
              const { UnitCollectionPage } = await import(
                'eds-product-experience-ux'
              );
              return {
                element: <UnitCollectionPage />,
                handle: {
                  title: 'Unit Page',
                  pageType: PAGE_TYPE.bundle,
                },
              };
            }}
          />
          <Route
            path="page/:sectionId"
            loader={async () => {
              const user = await queryClient.ensureQueryData(meQueryOptions());
              if (user?.userContext.currentRole === 'student') {
                return redirect('/student');
              }
              return null;
            }}
            lazy={async () => {
              const { PackageSectionPage } = await import(
                'eds-product-experience-ux'
              );
              return {
                element: <PackageSectionPage />,
                handle: {
                  title: 'Library - Explore ELA',
                  pageType: PAGE_TYPE.packageSection,
                },
              };
            }}
          />
          <Route
            path="page/:sectionId/part/:subsectionId"
            loader={async () => {
              const user = await queryClient.ensureQueryData(meQueryOptions());
              if (user?.userContext.currentRole === 'student') {
                return redirect('/student');
              }
              return null;
            }}
            lazy={async () => {
              const { PackageSectionSubsectionPage } = await import(
                'eds-product-experience-ux'
              );
              return {
                element: <PackageSectionSubsectionPage />,
                handle: {
                  title: 'Library - Explore ELA',
                  pageType: PAGE_TYPE.packageSection,
                },
              };
            }}
          />
          <Route
            path="assets/:id/*"
            loader={async ({ request }) => {
              const user = await queryClient.ensureQueryData(meQueryOptions());

              // Check access for student work routes
              const workRouteRedirect = await checkAccessForStudentWorkRoutes(
                request
              );
              if (workRouteRedirect) return workRouteRedirect;

              if (user.userContext.currentRole === 'student') {
                const rosterUser = user
                  ? await queryClient.ensureQueryData(
                      rosterUserQuery(user.userContext.userId)
                    )
                  : undefined;

                const grade = rosterUser?.grade;

                return {
                  user,
                  grade,
                };
              }

              return { user };
            }}
            lazy={async () => {
              const { TeacherStoryPage, StudentStoryPage } = await import(
                'eds-product-experience-ux'
              );
              const user = await queryClient.ensureQueryData(meQueryOptions());
              if (user?.userContext.currentRole !== 'student') {
                return {
                  element: <TeacherStoryPage />,
                  handle: {
                    title: storyPageTitle,
                    pageType: PAGE_TYPE.assetFamily,
                  },
                };
              }
              return {
                element: <StudentStoryPage />,
                handle: {
                  title: storyPageTitle,
                  pageType: PAGE_TYPE.assetFamily,
                },
              };
            }}
          />

          <Route
            path="recent"
            loader={async () => {
              const user = await queryClient.ensureQueryData(meQueryOptions());

              if (user?.userContext.currentRole !== 'student') {
                return redirect('/');
              }
              return user;
            }}
            lazy={async () => {
              const { StudentHistoryPage } = await import(
                'eds-product-experience-ux'
              );
              return {
                element: <StudentHistoryPage />,
                handle: {
                  title: 'Student History',
                  pageType: PAGE_TYPE.studentHistory,
                },
              };
            }}
          />
        </Route>
        {/* Teacher Protected Routes */}
        <Route loader={roleGuardLoader(['teacher', 'administrator'])}>
          <Route
            id="home"
            path="/"
            loader={homeDeepLinkLoader}
            lazy={async () => {
              const { HomePage } = await import('@/pages/HomePage/HomePage');
              return {
                element: <HomePage />,
              };
            }}
            handle={{ title: 'Home' }}
          />
          <Route
            path="/classes"
            loader={async () => {
              const { userContext: user } = await queryClient.ensureQueryData(
                meQueryOptions()
              );

              if (user.userSource === 'MyScholastic') {
                return redirect('/');
              }

              return user;
            }}
            lazy={async () => {
              const { RosterClassesPage } = await import('eds-roster-ux');
              return {
                element: <RosterClassesPage />,
                handle: {
                  title: 'Classes',
                  siteSection: SITE_SECTION.classes,
                },
              };
            }}
          />
          <Route
            path="/classes/details/:classId"
            lazy={async () => {
              const { RosterClassDetailsPage } = await import('eds-roster-ux');
              return {
                element: <RosterClassDetailsPage />,
                handle: { title: 'Class Details' },
              };
            }}
          />
          <Route
            path="/classes/details/:classId/student/:studentId"
            lazy={async () => {
              const { RosterStudentDetailsPage } = await import(
                'eds-roster-ux'
              );
              return {
                element: <RosterStudentDetailsPage />,
                handle: { title: 'Student Details' },
              };
            }}
          />

          <Route
            path="/subscriptions"
            lazy={async () => {
              const { SubscriptionsPage } = await import(
                '@/pages/SubscriptionsPage/SubscriptionsPage'
              );
              return {
                element: <SubscriptionsPage />,
                handle: {
                  title: 'Subscriptions',
                  siteSection: SITE_SECTION.subscriptions,
                },
              };
            }}
          />
        </Route>

        {/* Data Routes — teacher + admin (admin sees Coming Soon via DataRoleGate) */}
        <Route
          id="data-layout"
          loader={roleGuardLoader(['teacher', 'administrator'])}
          element={<DataRoleGate />}
          errorElement={<DataErrorBoundary />}
          shouldRevalidate={({ currentUrl, nextUrl }) =>
            currentUrl.pathname !== nextUrl.pathname
          }
        >
          {/* /data → redirect to product landing (or directly to overview if single app) */}
          <Route
            path="/data"
            loader={async () => {
              await ensureMultipleDataProducts();
              return redirect('/data/product-landing');
            }}
          />

          {/* /data/product-landing → product selection (skip if single app) */}
          <Route
            path="/data/product-landing"
            element={<ProductLandingPage />}
            handle={{ title: 'Data', siteSection: SITE_SECTION.data }}
            loader={async () => {
              await ensureMultipleDataProducts();
              return null;
            }}
          />

          {/* /data/:productId → report layout shell + all report routes */}
          <Route
            path="/data/:productId"
            element={<ReportLayout />}
            handle={{ siteSection: SITE_SECTION.data }}
            loader={async ({ params }) => {
              const productId = params.productId!;

              // Check 1: Is this app in our dashboard config?
              if (!isSupportedProduct(productId)) {
                // eslint-disable-next-line @typescript-eslint/no-throw-literal
                throw new Response('Not Found', { status: 404 });
              }

              // Check 2: Does the user have access from the fulfillment API?
              const applications = await queryClient.ensureQueryData(
                fulfillmentApplicationsQuery()
              );
              const hasAccess = applications.some(
                (app) => app.applicationCode === productId
              );
              if (!hasAccess) {
                // eslint-disable-next-line @typescript-eslint/no-throw-literal
                throw new Response('Not Found', { status: 404 });
              }

              return null;
            }}
          >
            {/* Default: redirect to overview */}
            <Route index loader={() => redirect('overview')} />

            {/* Overview report */}
            <Route
              path="overview"
              loader={allClassesViewGuardLoader('overview')}
              element={<OverviewPage />}
              handle={{ title: 'Overview' }}
            />
            <Route
              path="overview/class"
              loader={classViewGuardLoader('overview')}
              element={<OverviewPage />}
              handle={{ title: 'Class Overview' }}
            />
            <Route
              path="overview/student"
              loader={studentViewGuardLoader('overview')}
              element={<OverviewPage />}
              handle={{ title: 'Student Overview' }}
            />

            {/* Engagement report */}
            <Route
              path="engagement"
              loader={engagementFeedGuardLoader('engagement')}
              element={<EngagementFeedPage />}
              handle={{ title: 'Engagement Feed' }}
            />

            {/* Lexile report */}
            <Route
              path="lexile-measure"
              loader={allClassesViewGuardLoader('lexile-measure')}
              element={<LexilePage />}
              handle={{ title: 'Lexile' }}
            />
            <Route
              path="lexile-measure/class"
              loader={classViewGuardLoader('lexile-measure')}
              element={<LexilePage />}
              handle={{ title: 'Lexile — Class' }}
            />

            {/* GE report */}
            <Route
              path="ge-score"
              loader={allClassesViewGuardLoader('ge-score')}
              element={<GEPage />}
              handle={{ title: 'GE' }}
            />
            <Route
              path="ge-score/class"
              loader={classViewGuardLoader('ge-score')}
              element={<GEPage />}
              handle={{ title: 'GE — Class' }}
            />
          </Route>

          {/* Catch-all: any unmatched /data/* path → 404 */}
          <Route
            path="/data/*"
            loader={() => {
              // eslint-disable-next-line @typescript-eslint/no-throw-literal
              throw new Response('Not Found', { status: 404 });
            }}
          />
        </Route>

        {/* Site Auth Protected Routes */}
        <Route loader={roleGuardLoader(['site_auth', 'site_auth_admin'])}>
          <Route
            path="/siteauth"
            lazy={async () => {
              const { SiteAuthHomePage } = await import(
                '@/pages/HomePage/SiteAuthHomePage'
              );
              return {
                element: <SiteAuthHomePage />,
              };
            }}
            handle={{ title: 'SiteAuth Home' }}
          />
        </Route>

        {/* Student Protected Routes */}
        <Route
          path="/student"
          loader={roleGuardLoader(['student', 'classPassword'])}
        >
          <Route
            index
            loader={studentHomeDeepLinkLoader}
            lazy={async () => {
              const { StudentHomePage } = await import(
                '@/pages/HomePage/StudentHomePage'
              );
              return {
                element: <StudentHomePage />,
              };
            }}
            handle={{ title: 'Student Home' }}
          />
        </Route>

        {/* Admin Protected Routes */}
        <Route
          path="/users"
          handle={{ siteSection: SITE_SECTION.users }}
          loader={async () => {
            const permissions = await queryClient.ensureQueryData({
              queryKey: getPermissionsApiQueryKey(),
              queryFn: () => getPermissionsApi(),
            });

            if (!permissions?.manageAllUsers) {
              return redirect('/');
            }
            return null;
          }}
        >
          <Route
            index
            lazy={async () => {
              const { UsersLandingPage } = await import('eds-users-ux');

              return {
                handle: { title: 'Users' },
                element: <UsersLandingPage />,
              };
            }}
          />

          <Route
            path="/users/details/:userId"
            lazy={async () => {
              const { UserDetailsPage } = await import('eds-users-ux');

              return {
                handle: { title: 'User Details' },
                element: <UserDetailsPage />,
              };
            }}
          />
        </Route>

        <Route
          path="/omni-reader"
          lazy={async () => {
            const { OmniReaderPage } = await import(
              '@/pages/OmniReaderPage/OmniReaderPage'
            );
            return {
              element: <OmniReaderPage />,
              handle: {
                title: 'OmniReader',
              },
              loader: async () => {
                const user = (
                  await queryClient.ensureQueryData(meQueryOptions())
                ).userContext;
                return queryClient.ensureQueryData(
                  rosterUserQuery(user.userId)
                );
              },
            };
          }}
        />
        <Route
          path="/search"
          lazy={async () => {
            const { SearchPage } = await import(
              '@/pages/SearchPage/SearchPage'
            );
            return {
              element: <SearchPage />,
              handle: {
                title: 'Search Results',
              },
            };
          }}
        />
      </Route>

      <Route path="/login" loader={() => redirect('/sign-in')} />
      <Route path="/role" loader={() => redirect('/sign-in')} />
      <Route
        id="sign-in"
        path="/sign-in"
        loader={async () => queryClient.ensureQueryData(clientEnvsQuery())}
      >
        <Route
          index
          lazy={async () => {
            const { SignInPage } = await import('@authentication/index');
            return {
              element: (
                <SignInPage
                  onSignIn={(res) => {
                    const parsed = meResponseShape.safeParse(res);
                    if (parsed.success && parsed.data.sessionContext) {
                      queryClient.setQueryData(
                        meQueryOptions().queryKey,
                        parsed.data
                      );
                    } else {
                      queryClient.removeQueries({
                        queryKey: meQueryOptions().queryKey,
                      });
                    }
                  }}
                />
              ),
              handle: {
                title: 'Sign In',
              },
            };
          }}
        />
        <Route
          path="class-password"
          lazy={async () => {
            const { ClassroomPasswordPage } = await import(
              '@authentication/index'
            );
            return {
              element: (
                <ClassroomPasswordPage
                  onSignIn={() => {
                    queryClient.removeQueries({
                      queryKey: meQueryOptions().queryKey,
                    });
                  }}
                />
              ),
              handle: {
                title: 'Classroom Password Sign In',
              },
            };
          }}
        />
      </Route>
      <Route
        path="/google-onboard"
        lazy={async () => {
          const { GoogleOnboardLayout } = await import('@authentication/index');
          return {
            element: (
              <GoogleOnboardLayout>
                <Outlet />
              </GoogleOnboardLayout>
            ),
          };
        }}
      >
        <Route
          index
          lazy={async () => {
            const { GoogleOnboardStartPage } = await import(
              '@authentication/index'
            );
            return {
              element: <GoogleOnboardStartPage />,
              handle: {
                title: 'Google Classroom Onboarding',
              },
            };
          }}
        />
        <Route
          path="org-select"
          lazy={async () => {
            const { GoogleOnboardOrgSelectPage } = await import(
              '@authentication/index'
            );
            return {
              element: (
                <GoogleOnboardOrgSelectPage
                  onSignIn={() => {
                    queryClient.removeQueries({
                      queryKey: meQueryOptions().queryKey,
                    });
                  }}
                />
              ),
              handle: {
                title: 'Google Classroom - Select Organization',
              },
            };
          }}
        />
      </Route>
      <Route
        path="/sign-out"
        action={() => signOutAction(queryClient)}
        element={<SignOutPage />}
        handle={{ title: 'Sign Out' }}
        errorElement={<h1>Failed to sign out!</h1>}
      />
    </Route>

    <Route
      path="/*"
      element={
        <>
          <h1>Not Found</h1>
          <p>This page doesn&apos;t exist.</p>
        </>
      }
    />
  </>
);
